import Phaser from 'phaser';

import { StrokePath, DISTANCE_SAMPLE_SPACING } from './StrokePath';

import type { DirectionalCheckpoint } from '../objects/StrokeEvaluator';
import type { TracingGameStroke } from '../../types';

type SectionState = {
	curve: Phaser.Curves.Spline | Phaser.Curves.Line;
	length: number;
};

const PATH_COORDINATE_SCALE = 0.9;
const MIN_TANGENT_LENGTH_SQUARED = 1e-12;
const ARC_LENGTH_DIVISIONS_PER_POINT = 100;

const getCheckpointIntervalCount = (length: number, checkpointSpacing: number) =>
	Math.max(1, Math.ceil(length / checkpointSpacing));

export class StrokeGeometry {
	readonly path: Phaser.Curves.Path;
	readonly checkpoints: DirectionalCheckpoint[];
	readonly length: number;
	readonly trackingPath: StrokePath;

	private readonly sections: SectionState[];
	private readonly distanceSamples: Phaser.Math.Vector2[][];

	constructor(
		data: TracingGameStroke,
		drawingAreaWidth: number,
		drawingAreaHeight: number,
		checkpointSpacing: number,
	) {
		const [firstSection, ...followingSections] = data.path;

		if (!firstSection || firstSection.length < 2) {
			throw new Error('A tracing stroke first section must contain at least two points');
		}

		const toDrawingPoint = ([x, y]: [number, number]) =>
			new Phaser.Math.Vector2(
				drawingAreaWidth * (0.5 + (x - 0.5) * PATH_COORDINATE_SCALE),
				drawingAreaHeight * (0.5 + (y - 0.5) * PATH_COORDINATE_SCALE),
			);
		const firstPoints = firstSection.map(toDrawingPoint);

		this.path = new Phaser.Curves.Path(firstPoints[0].x, firstPoints[0].y);
		this.sections = [];

		let previousEnd = firstPoints.at(-1)!;
		this.addSection(firstPoints);

		followingSections.forEach((section, sectionIndex) => {
			if (section.length === 0) {
				throw new Error(`Tracing stroke section ${sectionIndex + 1} must contain at least one point`);
			}

			const points = [previousEnd, ...section.map(toDrawingPoint)];

			if (section.length <= 2) {
				for (let index = 1; index < points.length; index++) {
					this.addSection([points[index - 1], points[index]]);
				}
			} else {
				this.addSection(points);
			}
			previousEnd = points.at(-1)!;
		});

		this.length = this.sections.reduce((total, { length }) => total + length, 0);
		this.checkpoints = this.createCheckpoints(checkpointSpacing);
		this.distanceSamples = this.sections.map(({ curve, length }) => {
			const sampleCount = this.getSampleCount(length, checkpointSpacing);
			return Array.from({ length: sampleCount + 1 }, (_unused, index) => curve.getPointAt(index / sampleCount));
		});
		let sectionStart = 0;
		this.trackingPath = new StrokePath(
			this.distanceSamples.flatMap((samples, sectionIndex) => {
				const length = this.sections[sectionIndex].length;
				const points = samples.map((point, index) => ({
					point: { x: point.x, y: point.y },
					distance: sectionStart + (length * index) / (samples.length - 1),
				}));
				sectionStart += length;
				return sectionIndex === 0 ? points : points.slice(1);
			}),
		);
	}

	getDistanceTo(point: Phaser.Types.Math.Vector2Like) {
		let distance = Infinity;
		for (const samples of this.distanceSamples) {
			for (let index = 1; index < samples.length; index++) {
				const from = samples[index - 1];
				const to = samples[index];
				const dx = to.x - from.x;
				const dy = to.y - from.y;
				const lengthSquared = dx * dx + dy * dy;
				const progress =
					lengthSquared === 0
						? 0
						: Math.max(0, Math.min(1, ((point.x - from.x) * dx + (point.y - from.y) * dy) / lengthSquared));
				distance = Math.min(
					distance,
					Math.hypot(point.x - from.x - dx * progress, point.y - from.y - dy * progress),
				);
			}
		}
		return distance;
	}

	getCorridorIntervals(from: Phaser.Types.Math.Vector2Like, to: Phaser.Types.Math.Vector2Like, radius: number) {
		const intervals: { start: number; end: number }[] = [];
		const dx = to.x - from.x;
		const dy = to.y - from.y;
		const movementLengthSquared = dx * dx + dy * dy;
		const tolerance = 1e-7;
		const expandedRadius = radius + tolerance;
		if (movementLengthSquared === 0) return intervals;

		const addInterval = (start: number, end: number) => {
			start = Math.max(0, start);
			end = Math.min(1, end);
			if (start <= end) intervals.push({ start, end });
		};
		const addCircle = (center: Phaser.Types.Math.Vector2Like) => {
			const offsetX = center.x - from.x;
			const offsetY = center.y - from.y;
			const projection = (offsetX * dx + offsetY * dy) / movementLengthSquared;
			const perpendicularDistanceSquared = (offsetX * dy - offsetY * dx) ** 2 / movementLengthSquared;
			const remainingSquared = expandedRadius ** 2 - perpendicularDistanceSquared;
			if (remainingSquared < 0) return;
			const halfWidth = Math.sqrt(remainingSquared / movementLengthSquared);
			addInterval(projection - halfWidth, projection + halfWidth);
		};

		// Each sampled path segment contributes a rectangle and two circular end caps.
		for (const samples of this.distanceSamples) {
			for (const point of samples) addCircle(point);
			for (let index = 1; index < samples.length; index++) {
				const start = samples[index - 1];
				const end = samples[index];
				const length = Math.hypot(end.x - start.x, end.y - start.y);
				if (length === 0) continue;
				const tangentX = (end.x - start.x) / length;
				const tangentY = (end.y - start.y) / length;
				const offsetX = from.x - start.x;
				const offsetY = from.y - start.y;
				let entry = 0;
				let exit = 1;
				const clip = (position: number, velocity: number, minimum: number, maximum: number) => {
					if (velocity === 0) return position >= minimum && position <= maximum;
					const first = (minimum - position) / velocity;
					const second = (maximum - position) / velocity;
					entry = Math.max(entry, Math.min(first, second));
					exit = Math.min(exit, Math.max(first, second));
					return entry <= exit;
				};
				if (
					clip(offsetX * tangentX + offsetY * tangentY, dx * tangentX + dy * tangentY, 0, length) &&
					clip(
						-offsetX * tangentY + offsetY * tangentX,
						-dx * tangentY + dy * tangentX,
						-expandedRadius,
						expandedRadius,
					)
				)
					addInterval(entry, exit);
			}
		}

		intervals.sort((first, second) => first.start - second.start);
		const merged: typeof intervals = [];
		const progressTolerance = tolerance / Math.sqrt(movementLengthSquared);
		for (const interval of intervals) {
			const previous = merged.at(-1);
			if (previous && interval.start <= previous.end + progressTolerance) {
				previous.end = Math.max(previous.end, interval.end);
			} else {
				merged.push({ ...interval });
			}
		}
		return merged;
	}

	getStartPoint() {
		return this.path.getStartPoint();
	}

	getEndPoint() {
		return this.path.getEndPoint();
	}

	private addSection(points: Phaser.Math.Vector2[]) {
		const curve =
			points.length === 2 ? new Phaser.Curves.Line(points[0], points[1]) : new Phaser.Curves.Spline(points);
		curve.arcLengthDivisions = points.length * ARC_LENGTH_DIVISIONS_PER_POINT;
		const length = curve.getLength();

		this.path.add(curve);
		this.sections.push({ curve, length });
	}

	private getSampleCount(length: number, checkpointSpacing: number) {
		const checkpointIntervals = getCheckpointIntervalCount(length, checkpointSpacing);
		// Include each checkpoint in the same arc-length mapping used by tracking and the corridor.
		return checkpointIntervals * Math.max(1, Math.ceil(length / checkpointIntervals / DISTANCE_SAMPLE_SPACING));
	}

	private createCheckpoints(checkpointSpacing: number) {
		return this.sections.flatMap(({ length }, sectionIndex) => {
			const intervalCount = getCheckpointIntervalCount(length, checkpointSpacing);
			const firstIntervalIndex = sectionIndex === 0 ? 0 : 1;

			return Array.from({ length: intervalCount - firstIntervalIndex + 1 }, (_unused, index) =>
				this.createCheckpoint(sectionIndex, index + firstIntervalIndex, intervalCount),
			);
		});
	}

	private createCheckpoint(
		sectionIndex: number,
		intervalIndex: number,
		intervalCount: number,
	): DirectionalCheckpoint {
		const section = this.sections[sectionIndex];
		const position = intervalIndex / intervalCount;
		const point = section.curve.getPointAt(position);
		const tangent = this.getCheckpointTangent(sectionIndex, position);

		return {
			distanceFromPrevious: intervalIndex === 0 ? 0 : section.length / intervalCount,
			point: { x: point.x, y: point.y },
			tangent: { x: tangent.x, y: tangent.y },
		};
	}

	private getCheckpointTangent(sectionIndex: number, position: number) {
		const section = this.sections[sectionIndex];

		if (position < 1 || sectionIndex === this.sections.length - 1) {
			return section.curve.getTangentAt(position);
		}

		const incoming = section.curve.getTangentAt(1);
		const outgoing = this.sections[sectionIndex + 1].curve.getTangentAt(0);
		const bisector = incoming.add(outgoing);

		if (bisector.lengthSq() < MIN_TANGENT_LENGTH_SQUARED) {
			throw new Error(`Tracing stroke section ${sectionIndex + 1} makes a 180 degree reversal`);
		}

		return bisector.normalize();
	}
}
